import React, { useState } from 'react'
import { KibuCarePlan } from '@/services/api'
import { CheckCircle2, X } from 'lucide-react'
import KibuCareLearnMoreModal from './KibuCareLearnMoreModal'

export type KibuCarePlanDisplay = {
  planId: string
  title: string
  totalLabel: string
  perUnitLabel: string
}

type KibuCareSelectorProps = {
  planOptions: KibuCarePlanDisplay[]
  plans: KibuCarePlan[]
  selectedPlanId: string | null
  onSelectPlan: (planId: string) => void
  onClearPlan: () => void
  onDismiss?: () => void
  onNavigateAway?: () => void
  emptyMessage?: string | null
  className?: string
}

const KibuCareSelector: React.FC<KibuCareSelectorProps> = ({
  planOptions,
  plans,
  selectedPlanId,
  onSelectPlan,
  onClearPlan,
  onDismiss,
  onNavigateAway,
  emptyMessage,
  className = ''
}) => {
  const hasOptions = planOptions.length > 0 && plans.length > 0
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false)

  const getPlanImage = (planId: string): { src: string | null; alt: string } => {
    const plan = plans.find((entry) => entry._id === planId)
    if (!plan) {
      return { src: null, alt: 'Kibu Care' }
    }

    const sortedImages = (plan.images || [])
      .slice()
      .sort((a, b) => {
        const aOrder = typeof a === 'object' && a ? (a.order || 0) : 0
        const bOrder = typeof b === 'object' && b ? (b.order || 0) : 0
        return aOrder - bOrder
      })
    const firstImage = sortedImages[0]

    if (typeof firstImage === 'string') {
      return { src: firstImage, alt: plan.optionTitle || plan.productName || 'Kibu Care' }
    }

    if (firstImage && typeof firstImage === 'object') {
      return {
        src: firstImage.thumbnailUrl || firstImage.url || null,
        alt: firstImage.alt || plan.optionTitle || plan.productName || 'Kibu Care'
      }
    }

    return {
      src: typeof plan.heroImage === 'string' ? plan.heroImage : null,
      alt: plan.optionTitle || plan.productName || 'Kibu Care'
    }
  }

  const selectedOption = planOptions.find((option) => option.planId === selectedPlanId) || null
  const defaultOption = selectedOption || planOptions[0] || null

  if (!hasOptions && !emptyMessage) {
    return null
  }

  return (
    <div className={`relative rounded-lg border border-black/10 bg-kibuBlue p-3 ${className}`}>
      {onDismiss && (
        <button
          type="button"
          className="absolute right-2 top-2 rounded-full p-1 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
          onClick={onDismiss}
          aria-label="Dismiss Kibu Care"
        >
          <X className="size-4" />
        </button>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 justify-center">
          <div className="">
            <div className="flex flex-wrap items-baseline gap-x-1 gap-y-1">
              <h3 className="text-base font-semibold leading-none text-white">Kibu Care</h3>
              <p className="text-xs leading-none text-white">(Optional)</p>
              <button
                type="button"
                className="inline-flex h-5 items-center rounded-full border border-white/30 bg-white/10 px-2 text-[11px] font-semibold leading-none text-white transition-colors hover:bg-white/20"
                onClick={() => setShowLearnMoreModal(true)}
              >
                Learn more
              </button>
            </div>
            <p className="text-sm md:text-base text-white">Protect your headphones from accidental damage.</p>
            {/* <p className="mt-0.5 text-[11px] leading-snug text-gray-600">Accidental damage cover.</p> */}
          </div>
        </div>
        {selectedPlanId && (
          <button
            type="button"
            className="rounded-md border border-red-200 px-1.5 py-0.5 text-[11px] font-medium text-white hover:border-red-300 hover:text-black hover:bg-red-500"
            onClick={onClearPlan}
          >
            Remove
          </button>
        )}
      </div>
      {hasOptions ? (
        <div className="mt-3 grid grid-cols-2 gap-3">
          {planOptions.map((option) => {
            const isSelected = selectedPlanId === option.planId
            const planImage = getPlanImage(option.planId)
            return (
              <button
                type="button"
                key={option.planId}
                className={`relative w-full rounded-md border p-2 text-left transition-colors ${isSelected ? 'border-black bg-black text-white shadow-sm' : 'border-black/15 bg-white hover:border-black/40'}`}
                onClick={() => onSelectPlan(option.planId)}
                aria-pressed={isSelected}
              >
                <div className="flex items-center gap-2">
                  <div className="relative size-16 shrink-0 rounded-md overflow-hidden bg-slate-100">
                    {planImage.src ? (
                      <img
                        src={planImage.src}
                        alt={planImage.alt}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-slate-100 to-slate-200" />
                    )}
                    {isSelected && (
                      <div className="absolute inset-0 bg-kibuBlue/50 flex items-center justify-center">
                        <CheckCircle2 className="size-12 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold leading-tight">{option.title}</div>
                    {/* <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                      {option.perUnitLabel}
                    </div> */}
                    <div className="mt-2 text-base font-semibold leading-none">{option.totalLabel}</div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      ) : (
        <div className="mt-3 rounded-xl border border-dashed border-black/15 bg-white px-3 py-2 text-sm text-gray-600">
          {emptyMessage}
        </div>
      )}
      {hasOptions && <p className="mt-1 text-[10px] text-gray-200">One plan applies to eligible products.</p>}
      <KibuCareLearnMoreModal
        isOpen={showLearnMoreModal}
        onClose={() => setShowLearnMoreModal(false)}
        planOptions={planOptions}
        plans={plans}
        initialSelectedPlanId={defaultOption?.planId}
        onAddPlan={(planId) => onSelectPlan(planId)}
        onNoPlan={onClearPlan}
        onNavigateAway={onNavigateAway}
      />
    </div>
  )
}

export default KibuCareSelector
